import { useState } from 'react';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../hooks/useI18n';

interface ReadingFeedbackProps {
  feature: string;
  readingId?: string;
}

export function ReadingFeedback({ feature, readingId }: ReadingFeedbackProps) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [given, setGiven] = useState<'up' | 'down' | null>(null);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [insertedId, setInsertedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleFeedback(rating: 'up' | 'down') {
    if (given || saving || !user) return;
    setSaving(true);

    if (rating === 'up') {
      await supabase.from('reading_analytics').insert({
        user_id:    user.id,
        feature,
        reading_id: readingId ?? null,
        rating:     5,
      });
      setGiven('up');
      setSaving(false);
    } else {
      // Insert rating=1 and capture the inserted row id
      const { data } = await supabase
        .from('reading_analytics')
        .insert({
          user_id:    user.id,
          feature,
          reading_id: readingId ?? null,
          rating:     1,
        })
        .select('id')
        .single();

      setSaving(false);
      setInsertedId(data?.id ?? null);
      setShowModal(true);
    }
  }

  async function handleSubmitFeedback() {
    setSubmitting(true);
    if (insertedId && feedbackText.trim()) {
      await supabase
        .from('reading_analytics')
        .update({ metadata: { feedback_text: feedbackText.trim() } })
        .eq('id', insertedId);
    }
    setSubmitting(false);
    setShowModal(false);
    setGiven('down');
  }

  function handleSkip() {
    setShowModal(false);
    setGiven('down');
  }

  if (given) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1rem', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
        <span style={{ color: 'var(--petrol)' }}>✦</span>
        {t('shared.feedbackThanks')}
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)', marginTop: '1.25rem' }}>
        <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
          {t('shared.feedbackQuestion')}
        </span>
        <button
          onClick={() => handleFeedback('up')}
          aria-label={t('shared.feedbackQuestion')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: '0.375rem', borderRadius: 'var(--radius-sm)', minHeight: 'var(--touch-target)', minWidth: 'var(--touch-target)', justifyContent: 'center', transition: 'color 150ms' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#4ade80')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
        >
          <ThumbsUp size={18} />
        </button>
        <button
          onClick={() => handleFeedback('down')}
          aria-label="Não gostei"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: '0.375rem', borderRadius: 'var(--radius-sm)', minHeight: 'var(--touch-target)', minWidth: 'var(--touch-target)', justifyContent: 'center', transition: 'color 150ms' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
        >
          <ThumbsDown size={18} />
        </button>
      </div>

      {/* Negative feedback modal */}
      {showModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-modal-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(4px)',
          }}
          onClick={e => { if (e.target === e.currentTarget) handleSkip(); }}
        >
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-subtle)',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '440px',
            boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
              <div>
                <h2
                  id="feedback-modal-title"
                  className="font-display"
                  style={{ fontSize: 'var(--text-body)', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.25rem' }}
                >
                  Como podemos melhorar?
                </h2>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
                  Esta resposta é opcional mas muito valiosa para nós.
                </p>
              </div>
              <button
                onClick={handleSkip}
                aria-label="Fechar"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-sm)' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Textarea */}
            <textarea
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              placeholder="Descreva o que não te agradou na leitura..."
              rows={4}
              style={{
                width: '100%',
                resize: 'vertical',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.625rem 0.75rem',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-label)',
                fontFamily: 'inherit',
                outline: 'none',
                boxSizing: 'border-box',
                lineHeight: 1.5,
              }}
              onFocus={e => (e.currentTarget.style.borderColor = 'var(--petrol)')}
              onBlur={e => (e.currentTarget.style.borderColor = 'var(--border-subtle)')}
            />

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleSkip}
                disabled={submitting}
              >
                Pular
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSubmitFeedback}
                disabled={submitting}
              >
                {submitting ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
